import { adaptUserMeetingsPage } from '@/shared/api/user/meetingAdapter';
import { GoogleAdsAttribution, LinkedInAttribution } from '@/shared/lib/attribution';
import { Settings } from '@/shared/types/Settings';
import { ApiData, FeatureGroupRequest, User } from '@/shared/types/User';

import { BASE_URL } from '../constants';
import { del, get, patch, post, postPublic, put } from '../requestMethods';
import {
  ConfirmUserMeetingBookingResponse,
  CreateUserRequestData,
  RegisterUserMeetingBookingAttemptResponse,
  SaveGA4ClientIdResponse,
  SaveGoogleAdsAttributionResponse,
  UpdateUserRequestData,
} from './types';

const USER_URL = `${BASE_URL}/api/user`;
const API_DATA_URL = `${BASE_URL}/api/settings/api/credentials/`;

export const USER_API = {
  checkUserEligibility: (email: string) =>
    postPublic<{ eligible: boolean }>(`${USER_URL}/eligibility`, { data: { email } }),

  confirmUserMeetingBooking: (signal?: AbortSignal) =>
    post<ConfirmUserMeetingBookingResponse>(`${USER_URL}/meetings/confirm-booking`, { signal }),

  // Generate API data
  generateApiData: () => post<ApiData>(`${API_DATA_URL}`),

  registerUserMeetingBookingAttempt: (signal?: AbortSignal) =>
    post<RegisterUserMeetingBookingAttemptResponse>(`${USER_URL}/meetings/booking-attempt`, { signal }),

  addUserToFeatureGroup: (data: FeatureGroupRequest) =>
    post<FeatureGroupRequest>(`${USER_URL}/feature_group`, { data }),

  createUser: (data: CreateUserRequestData) => post<User>(USER_URL, { data }),

  deleteUser: () =>
    del<{
      ok: boolean;
    }>(USER_URL),

  // Get API data
  getApiData: () => get<ApiData>(`${API_DATA_URL}`),

  getHubspotToken: () => get<{ token: string }>(`${USER_URL}/hubspot-token`),

  getUser: () => get<User>(USER_URL),
  getUserFeatureGroups: () => get(`${USER_URL}/feature_groups`),
  getUserMeetings: (signal?: AbortSignal) =>
    get<unknown>(`${USER_URL}/meetings`, { signal }).then(adaptUserMeetingsPage),
  getUserSettings: () => get<Settings>(`${USER_URL}/settings`),
  saveGA4ClientId: (clientId: string, sessionId: string) =>
    post<SaveGA4ClientIdResponse>(`${USER_URL}/ga4-client-id`, {
      data: { ga4_client_id: clientId, ga4_session_id: sessionId },
    }),
  saveGoogleAdsAttribution: (data: GoogleAdsAttribution) =>
    put<SaveGoogleAdsAttributionResponse>(`${USER_URL}/gads`, { data }),
  saveLinkedInAttribution: (data: LinkedInAttribution) => put<LinkedInAttribution>(`${USER_URL}/linkedin`, { data }),
  setVoicePresetTutorialDisplayed: () => post<User>(`${USER_URL}/set_voice_preset_tutorial_displayed`),

  setVoiceSettingsTooltipDisplayed: () => post<User>(`${USER_URL}/set_instant_voice_clone_tooltip_displayed`),
  updateUser: (data: UpdateUserRequestData) => patch<User>(USER_URL, { data }),
};

export type {
  ConfirmUserMeetingBookingResponse,
  RegisterUserMeetingBookingAttemptResponse,
  UserMeeting,
  UserMeetingOwner,
  UserMeetingsPage,
} from './types';
